import React, { useState, useEffect, useMemo } from 'react';
import { useAccessLogs } from '../../hooks/useAccessLogs';
import { Helmet } from 'react-helmet';
import SecurityHeader from '../../components/ui/SecurityHeader';
import BottomTabNavigation from '../../components/ui/BottomTabNavigation';
import NotificationBanner from '../../components/ui/NotificationBanner';
import LogsFilterPanel from './components/LogsFilterPanel';
import LogsTable from './components/LogsTable';
import LogsStats from './components/LogsStats';
import BulkActions from './components/BulkActions';
import LogsPagination from './components/LogsPagination';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const AccessLogsPage = () => {
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    userId: '',
    doorId: '',
    action: '',
    anomaly: '',
    search: ''
  });

  // ✅ NEW: Realtime notification states
  const [newLogsCount, setNewLogsCount] = useState(0);
  const [showNewLogsNotification, setShowNewLogsNotification] = useState(false);
  const [previousLogsLength, setPreviousLogsLength] = useState(0);

  const [sortConfig, setSortConfig] = useState({
    key: 'timestamp',
    direction: 'desc'
  });

  const [selectedLogs, setSelectedLogs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [isExporting, setIsExporting] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Use real data from Supabase
  const { 
    logs: realLogs, 
    loading: logsLoading, 
    error: logsError, 
    stats: realStats,
    refetch: refetchLogs 
  } = useAccessLogs(filters);

  // Use real logs from Supabase
  const allLogs = useMemo(() => {
    return realLogs || [];
  }, [realLogs]);

  // ✅ NEW: Detect new logs and show notification
  useEffect(() => {
    if (!logsLoading && allLogs.length > 0) {
      if (previousLogsLength > 0 && allLogs.length > previousLogsLength) {
        const newCount = allLogs.length - previousLogsLength;
        setNewLogsCount(newCount);
        setShowNewLogsNotification(true);
        
        // Auto-hide after 10 seconds
        setTimeout(() => {
          setShowNewLogsNotification(false);
        }, 10000);
      }
      setPreviousLogsLength(allLogs.length);
    }
  }, [allLogs.length, logsLoading, previousLogsLength]);

  // ✅ NEW: Handler to load new logs
  const handleLoadNewLogs = () => {
    setShowNewLogsNotification(false);
    setNewLogsCount(0);
    setCurrentPage(1);
    refetchLogs();
  };

  // Filter and sort logs
  const filteredAndSortedLogs = useMemo(() => {
    let filtered = [...allLogs];

    // Search filter (if not already applied by hook)
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(log => {
        const searchableText = `${log.userId} ${log.doorId} ${log.doorName} ${log.action} ${log.notes || ''}`.toLowerCase();
        return searchableText.includes(searchTerm);
      });
    }

    // Sort logs
    filtered.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key === 'timestamp') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return filtered;
  }, [allLogs, filters.search, sortConfig]);

  // Paginated logs
  const paginatedLogs = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedLogs.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAndSortedLogs, currentPage, itemsPerPage]);

  // Use real stats from hook
  const stats = useMemo(() => {
    return realStats || {
      totalAttempts: 0,
      successfulAttempts: 0,
      failedAttempts: 0,
      anomalies: 0
    };
  }, [realStats]);

  const totalPages = Math.ceil(filteredAndSortedLogs.length / itemsPerPage);

  // Event handlers
  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
    setSelectedLogs([]);
  };

  const handleSort = (column) => {
    setSortConfig(prev => ({
      key: column,
      direction: prev.key === column && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSelectLog = (logId) => {
    setSelectedLogs(prev => 
      prev.includes(logId) 
        ? prev.filter(id => id !== logId)
        : [...prev, logId]
    );
  };

  const handleSelectAll = () => {
    if (selectedLogs.length === paginatedLogs.length) {
      setSelectedLogs([]);
    } else {
      setSelectedLogs(paginatedLogs.map(log => log.id));
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    
    try {
      // Create CSV content
      const csvHeaders = ['Timestamp', 'User ID', 'Door ID', 'Door Name', 'Action', 'Anomaly', 'IP Address', 'Device', 'Method', 'Duration (ms)', 'Notes'];
      const csvRows = filteredAndSortedLogs.map(log => [
        log.timestamp?.toISOString(),
        log.userId,
        log.doorId,
        log.doorName,
        log.action,
        log.isAnomaly ? 'Yes' : 'No',
        log.ipAddress,
        log.device,
        log.method,
        log.duration,
        log.notes || ''
      ]);

      const csvContent = [csvHeaders, ...csvRows].map(row => row.map(field => `"${field}"`).join(',')).join('\n');

      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `access-logs-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export logs');
    } finally {
      setIsExporting(false);
    }
  };

  const handleBulkAction = async (action) => {
    setIsProcessing(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      if (action === 'export') {
        const selectedLogsData = allLogs.filter(log => selectedLogs.includes(log.id));
        console.log('Exporting selected logs:', selectedLogsData);
      } else if (action === 'review') {
        console.log('Marking as reviewed:', selectedLogs);
      } else if (action === 'delete') {
        console.log('Deleting logs:', selectedLogs);
      }
      
      setSelectedLogs([]);
    } catch (error) {
      console.error('Bulk action failed:', error);
      alert('Failed to perform bulk action');
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setSelectedLogs([]);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    setSelectedLogs([]);
  };

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // Show loading state
  if (logsLoading && previousLogsLength === 0) {
    return (
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Access Logs - SecureLock Admin</title>
        </Helmet>
        <SecurityHeader />
        <NotificationBanner />
        <main className="pt-16 pb-20 px-4 max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="flex flex-col items-center space-y-4">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
              <p className="text-muted-foreground">Loading access logs from Supabase...</p>
            </div>
          </div>
        </main>
        <BottomTabNavigation />
      </div>
    );
  }

  // Show error state
  if (logsError) {
    return (
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Access Logs - SecureLock Admin</title>
        </Helmet>
        <SecurityHeader />
        <NotificationBanner />
        <main className="pt-16 pb-20 px-4 max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <p className="text-destructive font-semibold mb-2">Failed to load access logs</p>
              <p className="text-sm text-muted-foreground">{logsError}</p>
            </div>
          </div>
        </main>
        <BottomTabNavigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Access Logs - SecureLock Admin</title>
        <meta name="description" content="Comprehensive security audit trails with advanced filtering and anomaly detection capabilities" />
      </Helmet>
      <SecurityHeader />
      <NotificationBanner />

      {/* ✅ NEW: Floating Notification for New Logs */}
      {showNewLogsNotification && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 animate-slide-down">
          <div className="bg-accent text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-4 max-w-md">
            <div className="flex items-center space-x-3 flex-1">
              <Icon name="Bell" size={20} className="animate-pulse" />
              <span className="font-medium text-sm">
                {newLogsCount} new access log{newLogsCount > 1 ? 's' : ''} detected
              </span>
            </div>
            <Button
              onClick={handleLoadNewLogs}
              size="sm"
              className="bg-white text-accent hover:bg-gray-100 font-medium px-3 py-1 text-xs"
            >
              Load Now
            </Button>
            <button
              onClick={() => setShowNewLogsNotification(false)}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <Icon name="X" size={18} />
            </button>
          </div>
        </div>
      )}

      <main className="pt-16 pb-20 px-4 max-w-7xl mx-auto">
        <div className="space-y-6">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Access Logs</h1>
              <p className="text-muted-foreground mt-1">
                Real-time security audit trails from Supabase • {allLogs.length} total logs
              </p>
            </div>
          </div>

          {/* Stats Cards */}
          <LogsStats stats={stats} />

          {/* ✅ NEW: Inline New Logs Notification (Alternative) */}
          {newLogsCount > 0 && !showNewLogsNotification && (
            <div className="bg-accent/10 border border-accent rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Icon name="Info" className="text-accent" size={20} />
                  <span className="text-accent font-medium text-sm">
                    {newLogsCount} new log{newLogsCount > 1 ? 's' : ''} available since last refresh
                  </span>
                </div>
                <Button
                  onClick={handleLoadNewLogs}
                  size="sm"
                  iconName="RefreshCw"
                  className="bg-accent text-white hover:bg-accent/90"
                >
                  Refresh Logs
                </Button>
              </div>
            </div>
          )}

          {/* Filter Panel */}
          <LogsFilterPanel
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onExport={handleExport}
            totalLogs={allLogs.length}
            filteredLogs={filteredAndSortedLogs.length}
            isExporting={isExporting}
          />

          {/* Logs Table */}
          <LogsTable
            logs={paginatedLogs}
            onSort={handleSort}
            sortConfig={sortConfig}
            selectedLogs={selectedLogs}
            onSelectLog={handleSelectLog}
            onSelectAll={handleSelectAll}
            onLogDetails={() => {}}
          />

          {/* Pagination */}
          {totalPages > 1 && (
            <LogsPagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={filteredAndSortedLogs.length}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          )}
        </div>
      </main>
      
      {/* Bulk Actions */}
      <BulkActions
        selectedCount={selectedLogs.length}
        onExportSelected={() => handleBulkAction('export')}
        onMarkAsReviewed={() => handleBulkAction('review')}
        onDeleteSelected={() => handleBulkAction('delete')}
        onClearSelection={() => setSelectedLogs([])}
        isProcessing={isProcessing}
      />
      
      <BottomTabNavigation />
    </div>
  );
};

export default AccessLogsPage;